import React, { useEffect, useRef } from "react";
import { ROOM_SERVICE_MAP_KEYS } from "../consts";
import { useOnlinesSetup } from "../roomservice/presences";
import { useUserInfos } from "../roomservice/maps";
import * as Sentry from "@sentry/react";
import Plausible from "plausible-tracker";
import { v4 as uuidv4 } from "uuid";

const { trackEvent } = Plausible({
  domain: "sprout.place",
});

type Delay = number | null;
type TimerHandler = (...args: any[]) => void;

/**
 * Provides a declarative useInterval
 *
 * @param callback - Function that will be called every `delay` ms.
 * @param delay - Number representing the delay in ms. Set to `null` to "pause" the interval.
 */
const startTime = Date.now();
const useInterval = (callback: TimerHandler, delay: Delay) => {
  const savedCallbackRef = useRef<TimerHandler>();

  useEffect(() => {
    savedCallbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const handler = (...args: any[]) => savedCallbackRef.current!(...args);

    if (delay !== null) {
      const intervalId = setInterval(handler, delay);
      return () => clearInterval(intervalId);
    }
  }, [delay]);
};

function Telemetry() {
  const [onlines] = useOnlinesSetup();
  const userInfos = useUserInfos();

  const streamingUsersCount = onlines.filter(
    (id) => !!userInfos[ROOM_SERVICE_MAP_KEYS.USER + id]?.video
  ).length;

  // Send some usage data to sentry
  useInterval(() => {
    let ev: Sentry.Event = {
      event_id: uuidv4(),
      timestamp: Math.floor(Date.now() / 1000),
      platform: "javascript",
      message: "Call Ping",
      level: Sentry.Severity.Log,
      // type: Sentry.EventType
      tags: {
        users: onlines.length,
        streams: streamingUsersCount,
      },
      measurements: {
        users: { value: onlines.length },
        sessionLengthTenMinutes: {
          value: Math.round((Date.now() - startTime) / (1000 * 10 * 60)),
        },
        streams: { value: streamingUsersCount },
        tensOfBlocks: {
          value: Math.floor(document.querySelectorAll(".block").length / 10),
        },
      },
    };
    console.log(ev);
    Sentry.captureEvent(ev);

    trackEvent("Call-Ping", {
      props: {
        users: onlines.length.toString(),
        sessionLengthTenMinutes: Math.round(
          (Date.now() - startTime) / (1000 * 10 * 60)
        ).toString(),
        streams: streamingUsersCount.toString(),
        tensOfBlocks: Math.floor(
          document.querySelectorAll(".block").length / 10
        ).toString(),
      },
    });
  }, 10 * 60 * 1000); // Ping every 10 minutes

  return <></>;
}

export default Telemetry;
